/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useEffect } from "react";

// antd:
import { Checkbox, Form } from "antd";
// import { LoadingOutlined } from "@ant-design/icons";
// import type { CheckboxValueType } from "antd/es/checkbox/Group";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getAppState, getUser, changeAppState, onChangeTheme, onChangeCompact, onChangeShowDesktopIconsTitle, onChangeLng } from "../../libs/reducer.ts"; // getAppState
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
// import type {TelementState} from "../../types/uiapp.d.ts";
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type checkboxType = {
  indeterminate:boolean
};

// const { Title, Text, Link, Paragraph } = Typography;

const { rz } = window;

const RZcheckbox:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZcheckbox.displayName = "Checkbox";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const rlng = (lng === "en" ? "ru" : "en");
  const theme = useAppSelector(getTheme);

  const {rootAttributes, key, fullPath, content, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]); // format

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const hasSubCheckboxes = useMemo(() => Boolean(content && content.length), [content]);

  const chkType = useMemo<checkboxType>(() => {
    const typ:checkboxType = {indeterminate: false};
    (attributes.type || attributes.type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (tt === "indeterminate") { typ.indeterminate = true }
    });

    if (attributes.indeterminate) { typ.indeterminate = (attributes.indeterminate === "true") }
    return typ;
  }, [attributes]);

  const plainOptions = useMemo<string[]>(() => (
    (content && content.length)
      ? content.map((ch) => (
        ch.attributes["title_" + lng] || ch.attributes.title || ch.attributes["title_" + rlng] || ""
      ))
      : []
  ), [content, lng, rlng]);

  const checkedList = useMemo<string[]>(() => (
    (content && content.length)
      ? content
        .filter((ch) => (appState[ch.fullPath]?.value === "true" || appState[ch.fullPath]?.value === true))
        .map((ch) => (ch.attributes["title_" + lng] || ch.attributes.title || ch.attributes["title_" + rlng] || ""))
      : []
  ), [appState, content, lng, rlng]);

  const indeterminate = useMemo(() => (checkedList.length > 0 && checkedList.length < plainOptions.length), [checkedList, plainOptions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState.attributes["title2_" + lng] || elState.attributes.title2 || elState.attributes["title2_" + rlng]), [elState.attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const onChange = useCallback((event:CheckboxChangeEvent) => {
    if (fnc.config?.isDev) console.log({action: "<Checkbox key='" + key + "'> -> onChange()", value: event.target.checked});
    const fullPaths:Array<string> = [], newStates:Array<any> = [];
    (content || []).forEach((cnt) => {
      if (cnt && cnt.fullPath && appState[cnt.fullPath]) {
        fullPaths.push(cnt.fullPath); newStates.push({value: event.target.checked});
      }
    });

    const val = ((!content || !content.length) && chkType.indeterminate ? rz.switchCase([appState[fullPath].value === true, undefined, appState[fullPath].value === undefined, false, true]) : event.target.checked);
    fullPaths.push(fullPath); newStates.push({...elState, value: val});

    dispatch(changeAppState({appkey: rootAttributes.key, fullPath: fullPaths, newState: newStates}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      const values = newStates.map((st) => ({shortPath: st.shortPath, fullPath: st.fullPath, value: st.value}));
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: val, values}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret?.globalState) {
            if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: val, values}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret?.globalState) {
          if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
        }
      }
    }
  }, [key, content, chkType.indeterminate, fullPath, elState, dispatch, rootAttributes.key, rootNamespace, attributes, appState, user, lng, dct]);

  const onChangeSubCheck = useCallback((list) => {
    if (fnc.config?.isDev) console.log({action: "<Checkbox.Group key='" + key + "'> -> onChangeSubCheck()", value: list});
    const fullPaths:Array<string> = [], newStates:Array<any> = [];
    list.forEach((ch) => {
      const i = plainOptions.indexOf(ch as string);
      // console.log({i, fp: content[i].fullPath, as: appState[content[i].fullPath]});
      if (~i && content[i].fullPath && appState[content[i].fullPath]) {
        fullPaths.push(content[i].fullPath); newStates.push({value: true});
      }
    });
    plainOptions.forEach((po, o) => {
      const j = list.indexOf(po as string);
      if (!~j && content[o].fullPath && appState[content[o].fullPath]) {
        fullPaths.push(content[o].fullPath); newStates.push({value: false});
      }
    });
    fullPaths.push(fullPath); newStates.push({...elState, value: (list.length === plainOptions.length)});
    // console.log({fullPaths, newStates: newStates.map((st) => ({title: st?.attributes?.title_en, value: st.value}))});
    if (fullPaths.length) dispatch(changeAppState({appkey: rootAttributes.key, fullPath: fullPaths, newState: newStates}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      const values = newStates.map((st) => ({shortPath: st.shortPath, fullPath: st.fullPath, value: st.value}));
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, values}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, values}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, plainOptions, fullPath, elState, dispatch, rootAttributes.key, rootNamespace, attributes, content, appState, user, lng, dct]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // if (iType.rules) { frmItemProps.name = String(key); frmItemProps.rules = iType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  if (hasSubCheckboxes) {
    return (
      wrapperForm(
        <Fragment key={key + "-fragment"}>
          <Checkbox key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
            checked={elState.value === true || elState.value === "true"}
            indeterminate={indeterminate}
            onChange={onChange}
          >
            {title || title2}
          </Checkbox>
          {children}
          <Checkbox.Group key={key + "-checkbox-group"}
            options={plainOptions}
            value={checkedList}
            onChange={onChangeSubCheck}
          />
        </Fragment>
      )
    );
  }

  return (
    wrapperForm(
      <Checkbox key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
        checked={elState.value === true || elState.value === "true"}
        indeterminate={chkType.indeterminate && rz.isUndefinedOrNull(elState.value) ? true : undefined}
        onChange={onChange}
        disabled={elState?.disabled || permissions.disabled === true}
      >
        {title || ""}
      </Checkbox>
    )
  );
}

export default RZcheckbox;
/* eslint-enable @typescript-eslint/no-unused-vars */
